/**
 * Read More/Read Less Global Functionality
 * Handles expandable text content across the theme
 *
 * The preview is clamped with -webkit-line-clamp, which draws "…" whenever anything
 * comes after the last allowed line, even an empty line or paragraph. Descriptions from
 * the editor often end with <p><br></p> or similar, so "…" showed with nothing more to
 * read. Here we measure the real text instead: the clamp, "…" and the button are only
 * used when actual text is cut off. This is re-checked when the width changes and after
 * fonts load, because the number of lines depends on both.
 */

class ReadMoreManager {
  constructor() {
    this.init();
  }

  init() {
    this.initializeReadMore();
    this.setupObserver();
  }

  initializeReadMore() {
    const readMoreContainers = document.querySelectorAll('[data-read-more]');

    readMoreContainers.forEach((container) => {
      if (container.dataset.initialized) return;

      const toggle = container.querySelector('[data-toggle]');
      const preview = container.querySelector('[data-preview]');
      const fullText = container.querySelector('[data-full]');
      const moreText = toggle?.querySelector('.toggle-more');
      const lessText = toggle?.querySelector('.toggle-less');

      if (!toggle || !preview || !fullText) return;

      // Mark as initialized
      container.dataset.initialized = 'true';

      const elements = {
        toggle,
        preview,
        fullText,
        moreText,
        lessText,
        lineLimit: this.getLineLimit(preview),
      };

      // Set initial collapsed state so we can measure
      preview.classList.remove('hidden');
      fullText.classList.add('hidden');
      if (moreText) moreText.classList.remove('hidden');
      if (lessText) lessText.classList.add('hidden');
      toggle.setAttribute('aria-expanded', 'false');

      toggle.addEventListener('click', (e) => this.handleToggle(e, elements));

      const update = () => this.updateClamp(elements);
      update();

      if ('ResizeObserver' in window) {
        let lastWidth = preview.clientWidth;
        new ResizeObserver(() => {
          const width = preview.clientWidth;
          if (width === lastWidth) return;
          lastWidth = width;
          update();
        }).observe(container);
      }

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(update);
      }
    });
  }

  /**
   * Number of lines the preview is clamped to (set inline by snippets/text.liquid).
   * @param {HTMLElement} preview
   * @returns {number}
   */
  getLineLimit(preview) {
    const value = parseInt(preview.style.webkitLineClamp || preview.style.getPropertyValue('-webkit-line-clamp'), 10);
    return Number.isFinite(value) && value > 0 ? value : 3;
  }

  /**
   * Bottom edges of every line of real content (text, images, video) when the preview is
   * not clamped, measured on a hidden copy at the same width. Empty lines and empty
   * paragraphs are ignored.
   * @param {HTMLElement} preview
   * @returns {number[]}
   */
  getContentBottoms(preview) {
    const clone = /** @type {HTMLElement} */ (preview.cloneNode(true));
    clone.removeAttribute('data-preview');
    clone.setAttribute('aria-hidden', 'true');
    clone.style.cssText = `position:absolute;visibility:hidden;pointer-events:none;left:0;top:0;width:${preview.clientWidth}px;min-width:${preview.clientWidth}px;max-width:none;display:block;-webkit-line-clamp:unset;max-height:none;overflow:visible;`;
    preview.parentElement.appendChild(clone);

    const top = clone.getBoundingClientRect().top;
    const bottoms = [];

    const walker = document.createTreeWalker(clone, NodeFilter.SHOW_TEXT);
    const range = document.createRange();
    while (walker.nextNode()) {
      const node = walker.currentNode;
      // trim() also removes non-breaking spaces (&nbsp;)
      if (!node.textContent.trim()) continue;
      range.selectNodeContents(node);
      for (const rect of range.getClientRects()) {
        bottoms.push(rect.bottom - top);
      }
    }

    clone.querySelectorAll('img, video, iframe, svg, table').forEach((element) => {
      bottoms.push(element.getBoundingClientRect().bottom - top);
    });

    clone.remove();
    return bottoms;
  }

  /**
   * Uses the clamp ("…" + button) only when real text is cut off, and makes sure the "…"
   * ends a line of text rather than sitting alone on an empty line.
   * @param {object} elements
   */
  updateClamp({ toggle, preview, lineLimit }) {
    if (toggle.getAttribute('aria-expanded') === 'true') return;
    // Not rendered yet (e.g. inside a closed tab) — measure later, on resize
    if (!preview.clientWidth) return;

    // Clamped state, to know how much room the allowed lines take
    preview.style.display = '-webkit-box';
    preview.style.webkitLineClamp = String(lineLimit);
    preview.style.maxHeight = '';

    const bottoms = this.getContentBottoms(preview);
    const lastBottom = bottoms.reduce((max, value) => Math.max(max, value), 0);
    let visibleHeight = preview.clientHeight;

    if (lastBottom <= visibleHeight + 1) {
      // Everything readable fits: no "…" and no button. Empty lines past the limit stay hidden.
      preview.style.display = 'block';
      preview.style.webkitLineClamp = 'unset';
      preview.style.maxHeight = `${visibleHeight}px`;
      toggle.style.display = 'none';
      return;
    }

    // Real text is cut off. If the last allowed line is empty, stop a line earlier.
    const style = getComputedStyle(preview);
    const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2;
    let lines = lineLimit;
    while (lines > 1) {
      const lastVisible = bottoms
        .filter((value) => value <= visibleHeight + 1)
        .reduce((max, value) => Math.max(max, value), 0);
      if (visibleHeight - lastVisible < lineHeight * 0.8) break;
      lines -= 1;
      preview.style.webkitLineClamp = String(lines);
      visibleHeight = preview.clientHeight;
    }

    toggle.style.display = '';
  }

  handleToggle(event, elements) {
    event.preventDefault();
    event.stopPropagation();

    const { toggle, preview, fullText, moreText, lessText } = elements;
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      // Collapse - show preview, hide full text
      preview.classList.remove('hidden');
      fullText.classList.add('hidden');
      if (moreText) moreText.classList.remove('hidden');
      if (lessText) lessText.classList.add('hidden');
      toggle.setAttribute('aria-expanded', 'false');
      // The width may have changed while it was open
      this.updateClamp(elements);
    } else {
      // Expand - hide preview, show full text
      preview.classList.add('hidden');
      fullText.classList.remove('hidden');
      if (moreText) moreText.classList.add('hidden');
      if (lessText) lessText.classList.remove('hidden');
      toggle.setAttribute('aria-expanded', 'true');
    }
  }

  setupObserver() {
    // Watch for dynamically added content
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const readMoreElements = node.querySelectorAll
                ? node.querySelectorAll('[data-read-more]')
                : [];

              if (readMoreElements.length > 0) {
                this.initializeReadMore();
              }

              // Check if the added node itself is a read-more element
              if (node.dataset && node.dataset.readMore !== undefined) {
                this.initializeReadMore();
              }
            }
          });
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  // Public method to reinitialize (useful for AJAX content)
  reinitialize() {
    this.initializeReadMore();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.readMoreManager = new ReadMoreManager();
  });
} else {
  window.readMoreManager = new ReadMoreManager();
}

// Export for manual reinitialization
window.reinitReadMore = () => {
  if (window.readMoreManager) {
    window.readMoreManager.reinitialize();
  }
};
